<template>
  <div class="gender-mobility-dashboard">
    <div class="gender-layout">
      <aside class="gender-sidebar">
        <v-card class="gender-filter-card" outlined>
          <v-card-title class="gender-filter-card__title">
            Mobility and Gender Dashboard
          </v-card-title>
          <v-card-text class="gender-filter-card__body">
            <div class="availability-banner">
              Number of countries with available data for
              {{ currentDimensionLabel }}: {{ availableCountryCount }}
            </div>

            <div class="filter-label">Select Dimension</div>
            <v-select
              v-model="selectedDimensionId"
              :items="dimensionOptions"
              item-text="title"
              item-value="id"
              dense
              outlined
              hide-details="auto"
            />
          </v-card-text>
        </v-card>

        <v-card v-if="currentDimension" class="gender-description-card" outlined>
          <v-card-title class="section-title">Dimension Description</v-card-title>
          <v-card-text class="gender-description-card__body">
            <h3 class="dimension-title">{{ currentDimension.title }}</h3>
            <p class="dimension-description">{{ currentDimension.description }}</p>

            <div class="indicators-title">Indicators used to compute the score</div>
            <div v-if="currentDimension.indicators && currentDimension.indicators.length" class="indicator-table-wrap">
              <table class="indicator-table">
                <thead>
                  <tr>
                    <th>Indicators</th>
                    <th>Variable(s) used to compute indicators</th>
                    <th>Link(s)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="indicator in currentDimension.indicators"
                    :key="indicator.indicator"
                  >
                    <td>{{ indicator.indicator }}</td>
                    <td>{{ indicator.variables }}</td>
                    <td>
                      <span v-if="indicator.link_url">
                        <a :href="indicator.link_url" target="_blank" rel="noopener">
                          {{ indicator.link_label }}
                        </a>
                      </span>
                      <span v-else>{{ indicator.link_label }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-indicators-copy">
              No indicator rows were available for this dimension in the current source data.
            </div>
          </v-card-text>
        </v-card>
      </aside>

      <section class="gender-map-panel">
        <v-card class="gender-map-shell" outlined>
          <v-card-text class="gender-map-shell__body">
            <div v-if="loadingMap || loadingDashboard || loadingMetadata" class="map-placeholder">
              <v-progress-circular indeterminate color="primary" size="56" />
              <p>Loading gender and mobility data...</p>
            </div>
            <div v-else-if="mapError" class="map-placeholder">
              <v-icon size="48" color="error">mdi-alert-circle</v-icon>
              <p>{{ mapError }}</p>
            </div>
            <div v-else class="map-stage">
              <div class="map-stage__header">
                <div class="map-stage__heading">
                  {{ currentDimensionLabel }}
                  <span class="map-stage__heading-detail">(score)</span>
                </div>
              </div>

              <l-map
                ref="leafletMap"
                :zoom="zoom"
                :center="center"
                :options="mapOptions"
                class="gender-leaflet-map"
              >
                <l-control-zoom position="topright" />
                <l-tile-layer
                  :url="tileUrl"
                  :attribution="attribution"
                  :options="tileOptions"
                />
                <l-geo-json
                  v-if="geojsonData"
                  :geojson="geojsonData"
                  :options="geojsonOptions"
                />
              </l-map>

              <v-card class="gender-legend" outlined>
                <div class="gender-legend__item">
                  <span class="gender-legend__swatch" :style="{ backgroundColor: '#FFFFFF' }"></span>
                  No Data
                </div>
                <div class="gender-legend__item">
                  <span class="gender-legend__swatch" :style="{ backgroundColor: '#D6EEFF' }"></span>
                  0
                </div>
                <div class="gender-legend__item">
                  <span class="gender-legend__swatch" :style="{ backgroundColor: '#9FD0FB' }"></span>
                  0.33
                </div>
                <div class="gender-legend__item">
                  <span class="gender-legend__swatch" :style="{ backgroundColor: '#6EABEA' }"></span>
                  0.66
                </div>
                <div class="gender-legend__item">
                  <span class="gender-legend__swatch" :style="{ backgroundColor: '#4F76A2' }"></span>
                  1
                </div>
              </v-card>

              <div
                v-if="hoveredCountry"
                class="gender-tooltip"
                :style="{ left: `${tooltipX}px`, top: `${tooltipY}px` }"
              >
                {{ hoveredCountry.name }}: {{ hoveredCountry.valueLabel }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </section>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LGeoJson, LControlZoom } from 'vue2-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { loadJsonFromApi, loadWorldGeojson } from './worldMapLoader'

const COUNTRY_EQUIVALENTS = {
  usa: ['United States of America', 'United States', 'USA', 'US'],
  uk: ['United Kingdom', 'UK', 'Britain'],
  drc: ['DRC', 'Democratic Republic of Congo', 'Democratic Republic of the Congo'],
  czechia: ['Czechia', 'Czech Republic'],
  southkorea: ['South Korea', 'Republic of Korea', 'Korea, Republic of'],
  northkorea: ['North Korea', "Dem. Rep. Korea", "Korea, Democratic People's Republic of"],
  russia: ['Russia', 'Russian Federation'],
  turkey: ['Turkey', 'Turkiye'],
  taiwanprovinceofchina: ['Taiwan, Province of China', 'Taiwan'],
}

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

export default {
  name: 'GenderMobility',
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LControlZoom,
  },
  data() {
    return {
      loadingMetadata: true,
      loadingDashboard: true,
      loadingMap: true,
      mapError: null,
      metadata: null,
      dashboard: null,
      selectedDimensionId: 'D1',
      geojsonData: null,
      hoveredCountry: null,
      tooltipX: 0,
      tooltipY: 0,
      zoom: 2,
      center: [24, 8],
      tileUrl: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      tileOptions: { maxZoom: 18, subdomains: 'abcd' },
      mapOptions: {
        zoomControl: false,
        scrollWheelZoom: true,
        dragging: true,
        minZoom: 1,
        maxZoom: 10,
      },
    }
  },
  computed: {
    dimensionOptions() {
      return this.metadata?.dimensions || []
    },
    currentDimension() {
      return this.dimensionOptions.find(item => item.id === this.selectedDimensionId) || null
    },
    currentDimensionLabel() {
      return this.currentDimension?.title || ''
    },
    availableCountryCount() {
      return this.dashboard?.available_country_count ?? 0
    },
    rows() {
      return this.dashboard?.rows || []
    },
    mapRowsByKey() {
      const index = {}
      this.rows.forEach(row => {
        (row.country_keys || []).forEach(key => {
          const normalized = this.normalizeCountryKeySimple(key)
          if (normalized && !index[normalized]) index[normalized] = row
        })
      })
      return index
    },
    geojsonOptions() {
      return {
        style: this.getFeatureStyle,
        onEachFeature: this.onEachFeature,
      }
    },
  },
  watch: {
    selectedDimensionId() {
      this.loadDashboard()
    },
  },
  async mounted() {
    await Promise.all([this.loadMetadata(), this.loadGeojson()])
    await this.loadDashboard()
  },
  methods: {
    normalizeCountryKeySimple(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '')
    },
    normalizeCountryKey(value) {
      if (!value) return ''
      const compact = this.normalizeCountryKeySimple(value)
      const aliases = Object.values(COUNTRY_EQUIVALENTS).find(list =>
        list.some(item => this.normalizeCountryKeySimple(item) === compact)
      )
      return aliases ? this.normalizeCountryKeySimple(aliases[0]) : compact
    },
    async loadMetadata() {
      this.loadingMetadata = true
      try {
        this.metadata = await loadJsonFromApi(this.$domain, '/data/gender-mobility/metadata')
      } catch (error) {
        this.mapError = error.message
      } finally {
        this.loadingMetadata = false
      }
    },
    async loadDashboard() {
      this.loadingDashboard = true
      try {
        const params = new URLSearchParams()
        params.set('dimension_id', this.selectedDimensionId)
        this.dashboard = await loadJsonFromApi(this.$domain, `/data/gender-mobility/dashboard-data?${params.toString()}`)
      } catch (error) {
        this.mapError = error.message
      } finally {
        this.loadingDashboard = false
      }
    },
    async loadGeojson() {
      this.loadingMap = true
      try {
        this.geojsonData = await loadWorldGeojson(this.$domain)
      } catch (error) {
        this.mapError = error.message
      } finally {
        this.loadingMap = false
      }
    },
    lookupRowForFeature(feature) {
      const properties = feature.properties || {}
      const candidates = [
        properties.name,
        properties.iso3,
        properties.iso_3166_1_alpha_2_codes,
      ]
      for (const candidate of candidates) {
        const key = this.normalizeCountryKey(candidate)
        if (key && this.mapRowsByKey[key]) return this.mapRowsByKey[key]
      }
      return null
    },
    getFeatureStyle(feature) {
      const row = this.lookupRowForFeature(feature)
      return {
        fillColor: this.getFillColor(row ? row.value : null),
        weight: 1,
        opacity: 1,
        color: '#CFD8DC',
        fillOpacity: row ? 0.86 : 0.55,
      }
    },
    getFillColor(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return '#FFFFFF'
      if (value >= 0.66) return '#4F76A2'
      if (value >= 0.33) return '#6EABEA'
      if (value > 0) return '#9FD0FB'
      return '#D6EEFF'
    },
    onEachFeature(feature, layer) {
      layer.on({
        mouseover: event => this.handleMouseOver(event, feature),
        mousemove: event => this.handleMouseMove(event, feature),
        mouseout: () => this.handleMouseOut(layer, feature),
      })
    },
    handleMouseOver(event, feature) {
      const row = this.lookupRowForFeature(feature)
      this.hoveredCountry = {
        name: feature.properties?.name || 'Unknown',
        valueLabel: row ? Number(row.value).toFixed(2) : 'No Data',
      }
      this.tooltipX = event.originalEvent.clientX + 12
      this.tooltipY = event.originalEvent.clientY - 12
      event.target.setStyle({ weight: 2, color: '#607D8B' })
    },
    handleMouseMove(event, feature) {
      if (!this.hoveredCountry) return
      this.hoveredCountry.name = feature.properties?.name || 'Unknown'
      this.tooltipX = event.originalEvent.clientX + 12
      this.tooltipY = event.originalEvent.clientY - 12
    },
    handleMouseOut(layer, feature) {
      this.hoveredCountry = null
      layer.setStyle(this.getFeatureStyle(feature))
    },
  },
}
</script>

<style scoped>
.gender-mobility-dashboard {
  padding: 24px;
  height: calc(100vh - 88px);
  overflow: hidden;
  box-sizing: border-box;
}

.gender-layout {
  display: grid;
  grid-template-columns: 520px minmax(0, 1fr);
  gap: 20px;
  height: 100%;
  align-items: stretch;
}

.gender-sidebar {
  display: grid;
  gap: 16px;
  align-content: start;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
}

.gender-filter-card__title,
.section-title {
  font-size: 1.05rem;
  font-weight: 700;
}

.availability-banner {
  margin-bottom: 20px;
  padding: 12px 14px;
  background: #eaf3fb;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #263238;
}

.filter-label {
  font-size: 0.84rem;
  font-weight: 700;
  color: #37474f;
  margin-bottom: 8px;
}

.gender-description-card__body {
  padding-top: 0;
}

.dimension-title {
  margin: 0 0 8px;
  font-size: 1rem;
  line-height: 1.35;
  font-weight: 600;
  color: #37474f;
}

.dimension-description {
  white-space: pre-line;
  line-height: 1.52;
  color: #455a64;
  margin-bottom: 16px;
  font-size: 0.95rem;
}

.indicators-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #263238;
}

.indicator-table-wrap {
  overflow-x: auto;
}

.indicator-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.indicator-table th,
.indicator-table td {
  border: 1px solid #d9e1e8;
  padding: 12px 10px;
  text-align: left;
  vertical-align: top;
  font-size: 0.92rem;
  line-height: 1.45;
}

.indicator-table th {
  background: #f5f7fa;
  font-weight: 700;
}

.indicator-table td:first-child {
  font-weight: 700;
  width: 30%;
}

.indicator-table td:last-child {
  width: 18%;
}

.indicator-table a {
  color: #1565c0;
  text-decoration: none;
}

.indicator-table a:hover {
  text-decoration: underline;
}

.no-indicators-copy {
  color: #607d8b;
  font-size: 0.92rem;
}

.gender-map-panel {
  min-height: 0;
  height: 100%;
}

.gender-map-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.gender-map-shell__body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px;
  overflow: hidden;
}

.map-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #607d8b;
}

.map-stage {
  position: relative;
  height: 100%;
  min-height: 0;
}

.map-stage__header {
  position: absolute;
  top: 8px;
  left: 14px;
  z-index: 1000;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(207, 216, 220, 0.9);
  border-radius: 8px;
}

.map-stage__heading {
  font-size: 1rem;
  font-weight: 700;
  color: #263238;
}

.map-stage__heading-detail {
  font-size: 0.85rem;
  font-weight: 600;
  color: #607d8b;
}

.gender-leaflet-map {
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.gender-legend {
  position: absolute;
  right: 16px;
  bottom: 16px;
  z-index: 1000;
  padding: 12px 14px;
  min-width: 116px;
  background: rgba(255, 255, 255, 0.96);
}

.gender-legend__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #455a64;
  margin-bottom: 6px;
}

.gender-legend__item:last-child {
  margin-bottom: 0;
}

.gender-legend__swatch {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid rgba(38, 50, 56, 0.12);
}

.gender-tooltip {
  position: fixed;
  z-index: 1200;
  pointer-events: none;
  padding: 8px 10px;
  background: rgba(38, 50, 56, 0.92);
  color: #fff;
  border-radius: 8px;
  font-size: 0.82rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
}

@media (max-width: 1180px) {
  .gender-layout {
    grid-template-columns: 460px minmax(0, 1fr);
  }
}

@media (max-width: 980px) {
  .gender-mobility-dashboard {
    padding: 16px;
    height: auto;
    overflow: visible;
  }

  .gender-layout {
    grid-template-columns: 1fr;
    height: auto;
  }

  .gender-sidebar {
    height: auto;
    overflow: visible;
    padding-right: 0;
  }

  .gender-map-panel,
  .gender-map-shell {
    height: auto;
  }

  .gender-map-shell__body {
    height: 620px;
    min-height: 620px;
  }
}

@media (max-width: 640px) {
  .gender-map-shell__body {
    height: 500px;
    min-height: 500px;
    padding: 8px;
  }

  .gender-legend {
    right: 12px;
    bottom: 12px;
  }
}
</style>
